import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { PublicPageShell } from "@/components/PublicPageShell";
import { api } from "@/lib/api";
import { FileText, Shield, ScrollText } from "lucide-react";
import DOMPurify from "dompurify";
import { useSeo } from "@/lib/seo";

const DOC_META = {
  terms: {
    icon: ScrollText,
    label: { fr: "Conditions d'utilisation", en: "Terms of Service", es: "Términos y Condiciones", pt: "Termos e Condições" },
    desc: { fr: "Conditions Générales d'Utilisation du réseau social chrétien Yalola.", en: "Terms of Service of the Christian social network Yalola.", es: "Términos y Condiciones de la red social cristiana Yalola.", pt: "Termos e Condições da rede social cristã Yalola." },
  },
  privacy: {
    icon: Shield,
    label: { fr: "Confidentialité", en: "Privacy Policy", es: "Privacidad", pt: "Privacidade" },
    desc: { fr: "Politique de confidentialité de Yalola : données collectées, finalités, durées, droits RGPD.", en: "Yalola privacy policy: data collected, purposes, retention, GDPR rights.", es: "Política de privacidad de Yalola: datos recogidos, finalidades, plazos, derechos RGPD.", pt: "Política de privacidade da Yalola: dados recolhidos, finalidades, prazos, direitos RGPD." },
  },
  gdpr: {
    icon: FileText,
    label: { fr: "RGPD", en: "GDPR", es: "RGPD", pt: "RGPD" },
    desc: { fr: "Vos droits RGPD sur Yalola et comment les exercer.", en: "Your GDPR rights on Yalola and how to exercise them.", es: "Tus derechos RGPD en Yalola y cómo ejercerlos.", pt: "Os teus direitos RGPD na Yalola e como exercê-los." },
  },
};
const DOC_TYPES = ["terms", "privacy", "gdpr"];

export const Legal = () => {
  const { type } = useParams();
  const nav = useNavigate();
  const { hash } = useLocation();
  const { lang, config, t } = useApp();
  const [legal, setLegal] = useState(null);
  const active = DOC_TYPES.includes(type) ? type : "terms";

  useEffect(() => {
    api.get("/legal").then(({ data }) => setLegal(data.legal || {})).catch(() => setLegal({}));
  }, []);
  // Deep links such as /legal/privacy#kyc (from the identity consent step) scroll to the section once rendered.
  useEffect(() => {
    if (!legal || !hash) return;
    const tm = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    return () => clearTimeout(tm);
  }, [legal, hash, active, lang]);

  const lbl = (t) => DOC_META[t].label[lang] || DOC_META[t].label.fr;
  const content = legal?.[active]?.[lang] || legal?.[active]?.fr || "";
  useSeo({ title: lbl(active), description: DOC_META[active].desc[lang] || DOC_META[active].desc.fr, canonical: `/legal/${active}`, lang }, [active, lang]);

  return (
    <PublicPageShell testId="legal-page" maxWidth="max-w-3xl">
      <div>
        <div className="flex items-center gap-2 flex-wrap mb-5" data-testid="legal-doc-tabs">
          {DOC_TYPES.map((t) => {
            const Icon = DOC_META[t].icon;
            const on = t === active;
            return (
              <button
                key={t}
                onClick={() => nav(`/legal/${t}`, { replace: true })}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold border-2 transition-all"
                style={{
                  borderColor: on ? "var(--yalola-primary)" : "var(--yalola-border)",
                  background: on ? "var(--yalola-primary-soft)" : "transparent",
                  color: on ? "var(--yalola-primary)" : "var(--yalola-text-muted)",
                }}
                data-testid={`legal-tab-${t}`}
              >
                <Icon size={15} /> {lbl(t)}
              </button>
            );
          })}
        </div>

        {legal === null ? (
          <div className="text-sm py-12 text-center" style={{ color: "var(--yalola-text-muted)" }}>{t("legal.loading", "Chargement…")}</div>
        ) : (
          <article
            className="yalola-card p-6 legal-content"
            data-testid="legal-content"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content || "<p>—</p>") }}
          />
        )}
      </div>

      <div className="text-center py-4 text-xs space-y-2" style={{ color: "var(--yalola-text-muted)" }} data-testid="legal-more-links">
        <div className="flex items-center justify-center gap-3 flex-wrap">
          <Link to="/legal/child-safety" className="underline" data-testid="legal-child-safety-link">
            {({ fr: "Sécurité des enfants", en: "Child safety", es: "Seguridad infantil", pt: "Segurança infantil" })[lang] || "Sécurité des enfants"}
          </Link>
          <span aria-hidden="true">·</span>
          <Link to="/supprimer-mon-compte" className="underline" data-testid="legal-delete-account-link">
            {({ fr: "Supprimer mon compte", en: "Account deletion", es: "Eliminar cuenta", pt: "Excluir conta" })[lang] || "Supprimer mon compte"}
          </Link>
        </div>
        <div>© {new Date().getFullYear()} {config?.settings?.app_name || "Yalola"}</div>
      </div>

      <style>{`
        .legal-content h2 { font-size: 1.4rem; font-weight: 800; font-family: Outfit, sans-serif; margin-bottom: .5rem; color: var(--yalola-text); }
        .legal-content h3 { font-size: 1.05rem; font-weight: 700; margin-top: 1.25rem; margin-bottom: .35rem; color: var(--yalola-text); scroll-margin-top: 5rem; }
        .legal-content p, .legal-content li { font-size: .95rem; line-height: 1.65; color: var(--yalola-text); }
        .legal-content ul { list-style: disc; padding-left: 1.25rem; margin: .5rem 0; }
        .legal-content em { color: var(--yalola-text-muted); }
      `}</style>
    </PublicPageShell>
  );
};

export default Legal;
